import {ReactNode, useEffect, useRef, useState} from "react";
import {CircularProgress} from "@mui/material";
import {IconDesktop, IconPreviewOff, IconSmartphone} from "../../../assets";
import {useFormContext, useWatch} from "react-hook-form";
import {useParams} from "react-router-dom";
import {Box, Card, Colors, Text} from "@bounce/shared/ui";
import {ReachAccountSearchResult, useSearchAccounts} from "../../../apis/requests/accounts/searchAccounts";
import {useRenderTemplate} from "../../../apis/requests/templates/renderTemplate";
import {useRenderTemplateInline} from "../../../apis/requests/templates/renderTemplateInline";
import {ReachTemplateLanguage, ReachTemplateRenderResponse} from "../../../apis/requests/templates/types";
import {CONTENT_CHANNEL_LABELS, FormValues, MESSAGING_CHANNELS, NEEDS_LANDING_PAGE_SOURCE} from "../templateFormSchema";
import AccountInfoPanel from "./AccountInfoPanel";
import LandingPageView from "./LandingPageView";
import PhoneFrame from "./PhoneFrame";
import PreviewContent, {PreviewMode} from "./PreviewContent";
import RenderAsUserSection, {MIN_SEARCH_LENGTH} from "./RenderAsUserSection";

const DESKTOP_PREVIEW_WIDTH = 800;
const PREVIEW_HEIGHT = 620;

const PREVIEW_MODE_OPTIONS: {value: PreviewMode; icon: ReactNode}[] = [
  {value: "mobile", icon: <IconSmartphone width={18} height={18} />},
  {value: "desktop", icon: <IconDesktop width={18} height={18} />},
];

const supportsDesktopPreview = (channel: string | undefined) => !!channel && !MESSAGING_CHANNELS.has(channel);

const extractErrorMessage = (err: unknown): string => {
  if (err && typeof err === "object") {
    const e = err as {response?: {data?: {detail?: string}}; message?: string};
    return e.response?.data?.detail ?? e.message ?? "unknown error";
  }
  return "unknown error";
};

type TemplatePreviewPaneProps = {
  canPreviewUnsaved: boolean;
  selectedAccount: ReachAccountSearchResult | null;
  onSelectAccount: (account: ReachAccountSearchResult | null) => void;
  translationId?: number;
};

const TemplatePreviewPanel = ({
  canPreviewUnsaved,
  selectedAccount,
  onSelectAccount,
  translationId,
}: TemplatePreviewPaneProps) => {
  const {control} = useFormContext<FormValues>();
  const [contentChannel, templateKey, subject, preHeader, body, landingPageBody, channelMessageCode] = useWatch({
    control,
    name: [
      "content_channel",
      "template_key",
      "subject",
      "pre_header",
      "body",
      "landing_page_body",
      "channel_message_code",
    ],
  });
  const {id: templateIdParam} = useParams<{id?: string}>();
  const templateId = templateIdParam ? Number(templateIdParam) : undefined;
  const isSavedTemplate = templateId !== undefined;
  const canRenderUnsaved = !isSavedTemplate && canPreviewUnsaved;
  const showsAccountPicker = isSavedTemplate || canRenderUnsaved;

  const [searchInput, setSearchInput] = useState("");
  const [submittedQuery, setSubmittedQuery] = useState("");
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [previewMode, setPreviewMode] = useState<PreviewMode>("mobile");
  const [language, setLanguage] = useState<ReachTemplateLanguage>("en");
  const showLanguageToggle = isSavedTemplate && !!translationId;
  const renderTemplateId = language === "es" && translationId ? translationId : templateId;

  const handleSearchInputChange = (value: string) => setSearchInput(value);

  const clearSearch = () => {
    setSearchInput("");
    setSubmittedQuery("");
    setIsPickerOpen(false);
  };

  const handleSearch = () => {
    const q = searchInput.trim();
    if (q.length < MIN_SEARCH_LENGTH) return;
    setSubmittedQuery(q);
    setIsPickerOpen(true);
  };

  const {data: searchResults = [], isFetching: isSearching} = useSearchAccounts(
    {q: submittedQuery},
    {enabled: submittedQuery.length >= MIN_SEARCH_LENGTH}
  );

  const resultsMatchQuery = submittedQuery.length >= MIN_SEARCH_LENGTH && searchInput.trim() === submittedQuery;

  const {
    data: liveRenderResult,
    isFetching: isRenderingLive,
    error: liveRenderError,
  } = useRenderTemplate(
    {
      account_id: selectedAccount?.account_id ?? 0,
      template_id: renderTemplateId ?? 0,
    },
    {enabled: isSavedTemplate && !!selectedAccount, keepPreviousData: true}
  );

  const inlineRenderMutation = useRenderTemplateInline();
  const inlineResult = inlineRenderMutation.data ?? null;
  const inlineRenderError = inlineRenderMutation.error;
  const isRenderingInline = inlineRenderMutation.isPending;

  const runInlineRender = (account: ReachAccountSearchResult) => {
    if ((!body && !subject && !landingPageBody) || !templateKey) return;
    inlineRenderMutation.mutate({
      account_id: account.account_id,
      template_key: templateKey,
      content_channel: contentChannel,
      body,
      subject: subject || null,
      pre_header: preHeader || null,
      landing_page_body: landingPageBody || null,
      channel_message_code: channelMessageCode || null,
    });
  };

  const handleSelectAccount = (account: ReachAccountSearchResult | null) => {
    onSelectAccount(account);
    setIsPickerOpen(false);
    if (account) clearSearch();
    if (isSavedTemplate) return;
    if (!account) {
      inlineRenderMutation.reset();
      return;
    }
    runInlineRender(account);
  };

  const lastAutoRenderKey = useRef<string | null>(null);
  useEffect(() => {
    if (!canRenderUnsaved || !selectedAccount || !templateKey) return;
    const contentReady = MESSAGING_CHANNELS.has(contentChannel) ? !!subject : !!body;
    if (!contentReady) return;
    const key = `${selectedAccount.account_id}|${templateKey}|${contentChannel}|${channelMessageCode ?? ""}`;
    if (lastAutoRenderKey.current === key) return;
    lastAutoRenderKey.current = key;
    runInlineRender(selectedAccount);
  }, [canRenderUnsaved, selectedAccount, templateKey, contentChannel, channelMessageCode, subject, body]);

  const rawRenderResult: ReachTemplateRenderResponse | undefined = isSavedTemplate
    ? liveRenderResult ?? undefined
    : inlineResult ?? undefined;
  const renderResult =
    rawRenderResult && rawRenderResult.content_channel === contentChannel ? rawRenderResult : undefined;
  const hasRenderResult = !!renderResult;
  const isRendering = isSavedTemplate ? isRenderingLive : isRenderingInline;
  const renderError = isSavedTemplate ? liveRenderError : inlineRenderError;
  const isLandingPageChannel = NEEDS_LANDING_PAGE_SOURCE.has(contentChannel);

  return (
    <Card
      fullWidth
      sx={{
        alignItems: "stretch",
        gap: "16px",
        padding: "24px",
        borderRadius: "16px",
      }}
    >
      <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center"}}>
        <Text variant="interDisplay600" size={16} sx={{color: Colors.grey._700}}>
          Preview
        </Text>
        {supportsDesktopPreview(renderResult?.content_channel ?? contentChannel) && (
          <Box
            sx={{
              position: "relative",
              display: "inline-flex",
              backgroundColor: Colors.tertiary._200,
              borderRadius: "8px",
              border: `1px solid ${Colors.tertiary._200}`,
              p: "3px",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: 3,
                left: previewMode === "mobile" ? 3 : "50%",
                width: "calc(50% - 3px)",
                height: "calc(100% - 6px)",
                backgroundColor: Colors.bw.white,
                borderRadius: "6px",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                transition: "left 0.2s ease",
              }}
            />
            {PREVIEW_MODE_OPTIONS.map((option) => (
              <Box
                key={option.value}
                onClick={() => setPreviewMode(option.value)}
                sx={{
                  position: "relative",
                  zIndex: 1,
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "6px",
                  display: "flex",
                  alignItems: "center",
                  color: previewMode === option.value ? Colors.grey._900 : Colors.grey._500,
                  cursor: "pointer",
                  userSelect: "none",
                  transition: "color 0.2s ease",
                }}
              >
                {option.icon}
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {showsAccountPicker && (
        <RenderAsUserSection
          selected={selectedAccount}
          searchInput={searchInput}
          onSearchInputChange={handleSearchInputChange}
          onSearch={handleSearch}
          onSelect={handleSelectAccount}
          options={resultsMatchQuery ? searchResults : []}
          isSearching={isSearching}
          open={isPickerOpen}
          onOpenChange={setIsPickerOpen}
          showLanguageToggle={showLanguageToggle}
          language={language}
          onLanguageChange={setLanguage}
        />
      )}

      {selectedAccount && hasRenderResult && <AccountInfoPanel account={selectedAccount} />}

      {Boolean(renderError) && (
        <Box
          sx={{
            backgroundColor: Colors.red._100,
            border: `1px solid ${Colors.red._500}`,
            borderRadius: "8px",
            padding: "8px 12px",
          }}
        >
          <Text size={12} sx={{color: Colors.red._800}}>
            Render failed: {extractErrorMessage(renderError)}
          </Text>
        </Box>
      )}

      {!isSavedTemplate && !hasRenderResult ? (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5, py: 6}}>
          <IconPreviewOff width={32} height={32} style={{color: Colors.tertiary._400}} />
          <Text size={14} variant="inter600" sx={{color: Colors.grey._500, textAlign: "center"}}>
            {showsAccountPicker ? "Select an account to preview it" : "Sync from CIO or save the template to preview"}
          </Text>
        </Box>
      ) : isLandingPageChannel ? (
        <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 3, position: "relative"}}>
          <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1}}>
            <Text
              size={11}
              variant="inter600"
              sx={{color: Colors.grey._500, textTransform: "uppercase", letterSpacing: 0.5}}
            >
              {
                CONTENT_CHANNEL_LABELS[
                  (renderResult?.content_channel ?? contentChannel) as keyof typeof CONTENT_CHANNEL_LABELS
                ]
              }
            </Text>
            <PhoneFrame>
              <PreviewContent contentChannel={contentChannel} renderResult={renderResult} previewMode="mobile" />
            </PhoneFrame>
          </Box>
          <Box sx={{display: "flex", flexDirection: "column", alignItems: "center", gap: 1}}>
            <Text
              size={11}
              variant="inter600"
              sx={{color: Colors.grey._500, textTransform: "uppercase", letterSpacing: 0.5}}
            >
              Landing page
            </Text>
            <PhoneFrame>
              <LandingPageView html={renderResult?.rendered_landing_page ?? ""} />
            </PhoneFrame>
          </Box>
          {isRendering && (
            <Box sx={{position: "absolute", top: 8, right: 8}}>
              <CircularProgress size={16} sx={{color: Colors.grey._600}} />
            </Box>
          )}
        </Box>
      ) : (
        <Box sx={{display: "flex", justifyContent: "center", position: "relative"}}>
          {previewMode === "desktop" && supportsDesktopPreview(renderResult?.content_channel ?? contentChannel) ? (
            <Box
              sx={{
                width: "100%",
                maxWidth: `${DESKTOP_PREVIEW_WIDTH}px`,
                height: PREVIEW_HEIGHT,
                borderRadius: "8px",
                border: `1px solid ${Colors.tertiary._300}`,
                boxShadow: "0 6px 24px rgba(0, 0, 0, 0.12)",
                backgroundColor: Colors.bw.white,
                overflow: "auto",
              }}
            >
              <PreviewContent contentChannel={contentChannel} renderResult={renderResult} previewMode={previewMode} />
            </Box>
          ) : (
            <PhoneFrame>
              <PreviewContent contentChannel={contentChannel} renderResult={renderResult} previewMode="mobile" />
            </PhoneFrame>
          )}
          {isRendering && (
            <Box sx={{position: "absolute", top: 8, right: 8}}>
              <CircularProgress size={16} sx={{color: Colors.grey._600}} />
            </Box>
          )}
        </Box>
      )}
    </Card>
  );
};

export default TemplatePreviewPanel;
